/**
 * Puantaj Verme Modal'ı.
 * Tutar seçimi YOK — sistem ayarlardan otomatik çeker.
 * Sadece +/- yön + açıklama.
 */

import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { TrendingUp, TrendingDown } from 'lucide-react'
import { puantajOlustur } from '@/lib/services/puantaj-service'
import { ayarlariGetir } from '@/lib/services/ayarlar-service'
import { useAuth } from '@/contexts/AuthContext'
import type { KullaniciPersonel } from '@/types'

interface Props {
  acik: boolean
  personel: KullaniciPersonel | null
  onKapat: () => void
  onKaydedildi: () => void
}

type Yon = 'odul' | 'ceza'

export function PuantajVerModal({ acik, personel, onKapat, onKaydedildi }: Props) {
  const { kullanici, firebaseUser } = useAuth()
  const [yon, setYon] = useState<Yon>('odul')
  const [aciklama, setAciklama] = useState<string>('')
  const [tutar, setTutar] = useState<number>(0)
  const [hata, setHata] = useState<string | null>(null)
  const [yukleniyor, setYukleniyor] = useState(false)
  const [kaydediliyor, setKaydediliyor] = useState(false)

  const verenRol = kullanici?.rol === 'boss' ? 'boss' : 'yonetici'

  // Modal açıldığında ayarlardan tutarı çek
  useEffect(() => {
    if (!acik) return
    setYon('odul')
    setAciklama('')
    setHata(null)
    setYukleniyor(true)

    ayarlariGetir()
      .then((a) => {
        setTutar(verenRol === 'boss' ? a.puantajBossTutari : a.puantajSorumluTutari)
      })
      .catch((err) => {
        console.error(err)
        setHata('Ayarlar yüklenemedi.')
      })
      .finally(() => setYukleniyor(false))
  }, [acik, verenRol])

  async function kaydet(ev: React.FormEvent) {
    ev.preventDefault()
    if (!personel || !firebaseUser || !kullanici) return
    if (!aciklama.trim()) {
      setHata('Açıklama zorunlu.')
      return
    }

    setKaydediliyor(true)
    setHata(null)
    try {
      await puantajOlustur(
        {
          personelId: personel.id,
          tutar: yon === 'odul' ? tutar : -tutar,
          aciklama,
        },
        firebaseUser.uid,
        verenRol
      )
      onKaydedildi()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setKaydediliyor(false)
    }
  }

  if (!personel) return null

  return (
    <Modal acik={acik} onKapat={onKapat} baslik="Puantaj Ver" boyut="md">
      {yukleniyor ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : (
        <form onSubmit={kaydet} className="space-y-4">
          <div className="bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm">
            <p className="font-semibold text-brand-900">{personel.isim}</p>
          </div>

          {/* Yön seçici */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setYon('odul')}
              className={`p-4 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${
                yon === 'odul'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              <TrendingUp className="w-6 h-6" />
              <span className="text-sm font-bold">Ödül</span>
              <span className="text-xs">+{tutar} ₺</span>
            </button>
            <button
              type="button"
              onClick={() => setYon('ceza')}
              className={`p-4 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${
                yon === 'ceza'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              <TrendingDown className="w-6 h-6" />
              <span className="text-sm font-bold">Ceza</span>
              <span className="text-xs">−{tutar} ₺</span>
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Açıklama <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              value={aciklama}
              onChange={(e) => setAciklama(e.target.value)}
              className="input-base"
              placeholder={
                yon === 'odul'
                  ? 'Örn: Mal kabulü hızlı yaptın, teşekkürler!'
                  : 'Örn: Kasadan eksik para'
              }
              autoFocus
            />
          </div>

          <div
            className={`rounded-xl p-3 text-center font-semibold ${
              yon === 'odul'
                ? 'bg-green-50 text-green-900 border border-green-200'
                : 'bg-red-50 text-red-900 border border-red-200'
            }`}
          >
            {yon === 'odul' ? '+' : '−'} {tutar} ₺ {yon === 'odul' ? 'eklenecek' : 'düşülecek'}
          </div>

          {verenRol === 'boss' && (
            <p className="text-xs text-gray-500 text-center">
              Tutarı değiştirmek için Ayarlar &gt; Puantaj sayfasını kullan.
            </p>
          )}

          {hata && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
              {hata}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onKapat}
              className="btn-secondary flex-1"
              disabled={kaydediliyor}
            >
              İptal
            </button>
            <button type="submit" className="btn-primary flex-1" disabled={kaydediliyor}>
              {kaydediliyor ? 'Kaydediliyor...' : 'Onayla'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
